/**
 * React Query Hooks for Matters
 *
 * This module provides React hooks for matter operations using React Query,
 * with optimistic updates and proper cache management.
 *
 * Query hooks use directApiClient for direct API calls (faster, no server action overhead).
 * Mutation hooks still use server actions for revalidation support.
 */

'use client';

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions
} from '@tanstack/react-query';
import { toast } from '@/lib/toast';
import { FRESH_DATA_CONFIG } from '../query-config';
import { createMutationHook, extractMutationData } from '../mutation-utils';
import { getDependencies } from '../query-dependencies';
import { directApiClient } from '@/lib/api/direct-client';
import { matterKeys } from '@/lib/query-keys';
import {
  createMatterAction,
  updateMatterAction,
  deleteMatterAction,
  fetchMatterDeletionEligibility
} from './client';
import type {
  Matter,
  MatterFilters,
  MatterListResponse,
  MatterSummary,
  CreateMatterRequest,
  UpdateMatterRequest
} from './types';

// Query Hooks

/**
 * Hook to fetch list of matters with optional filters
 * Uses directApiClient for direct API calls (no server action overhead)
 *
 * @param filters - Optional filters for the query
 * @param options - Optional React Query options
 */
export function useMatters(
  filters?: MatterFilters,
  options?: { initialData?: MatterListResponse; enabled?: boolean }
) {
  return useQuery({
    queryKey: matterKeys.list(filters),
    queryFn: async () => {
      // Build query params
      const params: Record<string, string | number | boolean> = {};

      if (filters?.q) params.q = filters.q;
      if (filters?.search && !filters?.q) params.search = filters.search;
      if (filters?.includeArchived !== undefined)
        params.includeArchived = filters.includeArchived;
      if (filters?.assignedToMe !== undefined)
        params.assignedToMe = filters.assignedToMe;
      if (filters?.hasTask !== undefined) params.hasTask = filters.hasTask;
      if (filters?.page) params.page = filters.page;
      if (filters?.limit) params.limit = filters.limit;
      // Backend filter params
      if (filters?.types?.length) {
        params.types = filters.types.join(',');
      }
      if (filters?.teamMemberIds?.length) {
        params.teamMemberIds = filters.teamMemberIds.join(',');
      }
      if (filters?.tags?.length) {
        params.tags = filters.tags.join(',');
      }
      if (filters?.sortBy) params.sortBy = filters.sortBy;
      if (filters?.sortOrder) params.sortOrder = filters.sortOrder;
      if (filters?.isDemo !== undefined) params.isDemo = filters.isDemo;

      const response = await directApiClient.get<{
        items: MatterSummary[];
        meta: {
          totalItems: number;
          itemCount: number;
          itemsPerPage: number;
          totalPages: number;
          currentPage: number;
        };
      }>('/matters', { params });

      // Transform to MatterListResponse format for compatibility
      return {
        matters: response.items,
        total: response.meta.totalItems ?? response.meta.itemCount,
        page: response.meta.currentPage,
        limit: response.meta.itemsPerPage,
        hasMore: response.meta.totalPages
          ? response.meta.currentPage < response.meta.totalPages
          : false
      } as MatterListResponse;
    },
    ...FRESH_DATA_CONFIG,
    staleTime: 2 * 1000,
    enabled: options?.enabled ?? true,
    ...(options?.initialData && { initialData: options.initialData })
  });
}

/**
 * Hook to fetch the demo matters (DEMO-001 etc.) used by the Getting
 * Started hub. Returns a `byReference` map so consumers can look up
 * a matter by its stable `lawFirmInternalReference` (the matter id is
 * dynamic across environments).
 */
export function useDemoMatters() {
  const query = useMatters({ isDemo: true, limit: 10 }, undefined);

  const byReference = (query.data?.matters ?? []).reduce<
    Record<string, MatterSummary>
  >((acc, matter) => {
    if (matter.lawFirmInternalReference) {
      acc[matter.lawFirmInternalReference] = matter;
    }
    return acc;
  }, {});

  return {
    ...query,
    matters: query.data?.matters ?? [],
    byReference,
    /** Resolve a demo matter by reference (e.g. 'DEMO-001'). */
    getByReference: (reference: string) => byReference[reference]
  };
}

/**
 * Hook to fetch a single matter by ID
 * Uses directApiClient for direct API calls (no server action overhead)
 */
export function useMatter(
  matterId: number,
  options?: {
    enabled?: boolean;
    /**
     * React Query poll interval, same shape as `useQuery`'s `refetchInterval`
     * (fixed ms, `false` to disable, or a callback returning ms | false). Used
     * by the matter layout to poll while a Smokeball outward sync is pending so
     * the header's "Synced" status auto-appears (see `isPmsSyncPending`). It is
     * per-observer, so only the observer that sets it polls — other `useMatter`
     * consumers are unaffected.
     */
    refetchInterval?: UseQueryOptions<Matter>['refetchInterval'];
  }
) {
  return useQuery({
    queryKey: matterKeys.detail(matterId),
    queryFn: async () => {
      return directApiClient.get<Matter>(`/matters/${matterId}`);
    },
    enabled: !!matterId && (options?.enabled ?? true),
    refetchOnMount: 'always', // No more caching
    refetchInterval: options?.refetchInterval
  });
}

/**
 * Whether the firm can delete a matter (VALE-1927). Fetched when the delete dialog opens,
 * never per list row, and always fresh: the answer changes the moment anything is sent.
 */
export function useMatterDeletionEligibility(
  matterId: number,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: matterKeys.deletionEligibility(matterId),
    queryFn: () => fetchMatterDeletionEligibility(matterId),
    enabled: !!matterId && (options?.enabled ?? true),
    staleTime: 0,
    refetchOnMount: 'always'
  });
}

// Mutation Hooks

/**
 * Hook to create a new matter
 */
export function useCreateMatter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateMatterRequest) => {
      const result = await createMatterAction(data);
      return extractMutationData(result);
    },
    onSuccess: (matter) => {
      // Invalidate the matters list
      queryClient.invalidateQueries({ queryKey: matterKeys.lists() });

      // If we have the created matter ID, also invalidate its detail and dependencies
      if (matter?.id) {
        queryClient.invalidateQueries({
          queryKey: matterKeys.detail(matter.id)
        });
        // Also invalidate all dependencies for this matter
        const dependencies = getDependencies('matter', matter.id);
        dependencies.forEach((key) => {
          queryClient.invalidateQueries({ queryKey: key });
        });
      }

      // No success toast - user is redirected to the new matter page
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to create matter');
      console.error('Create matter error:', error);
    }
  });
}

/**
 * **The matter merge, as a pure function so the shape rule can be tested.**
 *
 * `UpdateMatterRequest` is NOT `Partial<Matter>`, and spreading it as one is a
 * crash rather than a stale read. Three keys carry a request shape the record
 * does not share, so they are dropped here and left to the refetch:
 *
 * - `roles` is `{ userId, role }[]` on the way out and `MatterRoleAssignment[]`
 *   (`{ user, role, assignedAt }`) on the record. Writing the request shape into
 *   the cache makes every reader that dereferences `assignment.user` throw,
 *   `build-matter-header-data.ts` among them, which the whole matter route
 *   renders through. Changing the acting solicitor would have taken it down.
 * - `metadata` is a partial on the way out and would replace the record's object
 *   wholesale, dropping `metadata.pms` and with it the sync state the layout
 *   polls on.
 * - `clientIds` has no counterpart at all; the record holds `clients`.
 *
 * `officeId` DOES belong in the merge, but not alone: the read-out composes
 * `officeName`, so the id without the name paints the PREVIOUS office's name
 * against the new id. It is derived from the roster the record already carries,
 * the same way the lead and account merges derive theirs (VALE-1505).
 */
export function applyOptimisticMatterUpdate(
  snapshot: Matter,
  data: UpdateMatterRequest
): Matter {
  const { roles, metadata, clientIds, ...mergeable } =
    data as UpdateMatterRequest & { clientIds?: unknown };
  void roles;
  void metadata;
  void clientIds;
  const next: Matter = { ...snapshot, ...(mergeable as Partial<Matter>) };
  if (mergeable.officeId !== undefined) {
    next.officeName =
      snapshot.offices?.find((office) => office.id === mergeable.officeId)
        ?.name ?? null;
  }
  return next;
}

/**
 * Hook to update an existing matter
 */
export function useUpdateMatter(options?: { showToast?: boolean }) {
  const showToast = options?.showToast ?? true;
  const queryClient = useQueryClient();

  const useHook = createMutationHook<
    Matter,
    {
      matterId: number;
      data: UpdateMatterRequest;
    },
    { snapshot: Matter | undefined }
  >({
    mutationFn: async ({ matterId, data }) => {
      const result = await updateMatterAction(matterId, data);
      return extractMutationData(result);
    },
    /*
      **Land the committed value in the matter the surfaces render from before
      the request leaves** (`components/attributes/CLAUDE.md` §6, VALE-709).
      Four attribute surfaces commit through this hook, the Overview's estate
      card, the Manage Estate Details drawer, the enquiry band's acting
      solicitor, and each closes its editor the instant the commit is called,
      so an invalidate-only hook repainted the reader's PREVIOUS value for the
      whole round trip plus the refetch, which is the exact symptom a firm
      reported as "my edit was thrown away". The refetch reconciles anything the
      server derives, and a refusal rolls the cache back by refetching (see
      `createMutationHook`). VALE-1505.

      **`UpdateMatterRequest` is NOT `Partial<Matter>`, and spreading it as one
      is a crash, not a stale read.** Three keys carry a request shape that the
      record does not share, so they are excluded here and left to the refetch:

      - `roles` is `{ userId, role }[]` on the way out and
        `MatterRoleAssignment[]` (`{ user, role, assignedAt }`) on the record.
        Writing the request shape into the cache makes every reader that
        dereferences `assignment.user` throw, `build-matter-header-data.ts`
        among them, which the whole matter route renders through. Changing the
        acting solicitor would have taken the route down.
      - `metadata` is a partial on the way out and would replace the record's
        object wholesale, dropping `metadata.pms` and with it the sync state the
        layout polls on.
      - `clientIds` has no counterpart at all; the record holds `clients`.

      `officeId` DOES belong in the merge, but not alone: the read-out composes
      `officeName`, so the id without the name paints the previous office's
      name against the new id. It is derived here from the roster the record
      already carries, the same way the lead and account merges derive theirs.
    */
    optimisticUpdate: ({ matterId, data }) => {
      const key = matterKeys.detail(matterId);
      const snapshot = queryClient.getQueryData<Matter>(key);
      if (snapshot) {
        queryClient.setQueryData<Matter>(
          key,
          applyOptimisticMatterUpdate(snapshot, data)
        );
      }
      return { snapshot };
    },
    // Both, deliberately (VALE-216). `refetchKeys` refetches with
    // `type: 'active'`, so it only reaches queries that are mounted right now —
    // an unmounted matter-dependent query (a closed sheet, a tab not on screen)
    // keeps serving cached data until its `staleTime` lapses. `invalidateKeys`
    // is what marks those stale so they refetch on their next mount. This hook
    // set only `refetchKeys`, which is why a jurisdiction change on the Grant
    // tab left the Redemption tab's curated form set — and its Section 1071B
    // banner — stale. Matches the `contact` / `inventory-item` hooks.
    invalidateKeys: (variables) =>
      getDependencies('matter', variables.matterId),
    refetchKeys: (variables) => getDependencies('matter', variables.matterId),
    onSuccessMessage: () => 'Matter updated successfully',
    onErrorMessage: 'Failed to update matter',
    showToast
  });

  return useHook();
}

/**
 * Hook to delete a matter
 */
export function useDeleteMatter() {
  const useHook = createMutationHook<void, number>({
    mutationFn: async (matterId) => {
      const result = await deleteMatterAction(matterId);
      if (!result.success) {
        throw new Error(result.error || 'Failed to delete matter');
      }
    },
    refetchKeys: () => [matterKeys.lists()],
    onSuccessMessage: () => 'Matter deleted successfully',
    onErrorMessage: 'Failed to delete matter'
  });

  return useHook();
}

/**
 * Hook to update matter tags with optimistic updates
 * Prevents race condition when adding/removing tags
 */
export function useMatterTags() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      matterId,
      tagsToAdd,
      tagsToRemove
    }: {
      matterId: number;
      tagsToAdd: string[];
      tagsToRemove: string[];
    }) => {
      const { addTagToMatter, removeTagFromMatter } =
        await import('../tags/hooks');

      for (const tag of tagsToAdd) {
        await addTagToMatter(matterId, tag);
      }

      for (const tag of tagsToRemove) {
        await removeTagFromMatter(matterId, tag);
      }

      return { matterId, tagsToAdd, tagsToRemove };
    },

    // Optimistic update to prevent race condition
    onMutate: async ({ matterId, tagsToAdd, tagsToRemove }) => {
      // Cancel any outgoing refetches to prevent race condition
      await queryClient.cancelQueries({ queryKey: ['tasks'] });

      // Snapshot the previous value for rollback
      const previousTasks = {
        today: queryClient.getQueryData(['tasks', 'today']),
        upcoming: queryClient.getQueryData(['tasks', 'upcoming'])
      };

      // Optimistically update the cache
      queryClient.setQueriesData({ queryKey: ['tasks'] }, (old: unknown) => {
        if (!Array.isArray(old)) return old;

        return old.map(
          (
            task: { matterId: number; matter?: { tags?: string[] } } & Record<
              string,
              unknown
            >
          ) => {
            if (task.matterId !== matterId) return task;

            const currentTags = task.matter?.tags || [];

            // Calculate new tags: remove tagsToRemove, add tagsToAdd
            const newTags = [
              ...currentTags.filter((t: string) => !tagsToRemove.includes(t)),
              ...tagsToAdd
            ];

            return {
              ...task,
              matter: {
                ...task.matter,
                tags: newTags
              }
            };
          }
        );
      });

      return { previousTasks };
    },

    onSuccess: () => {
      // DON'T invalidate queries - cache is already correct from optimistic update
      // Next natural refetch will sync with backend
      // This prevents the race condition entirely

      // Invalidate tags list for autocomplete/filters
      queryClient.invalidateQueries({ queryKey: ['tags'] });
    },

    onError: (error: Error, variables, context) => {
      // Only log in development
      if (process.env.NODE_ENV === 'development') {
        console.error('Tags update error:', error);
      }

      // Rollback optimistic updates on error
      if (context?.previousTasks) {
        queryClient.setQueryData(
          ['tasks', 'today'],
          context.previousTasks.today
        );
        queryClient.setQueryData(
          ['tasks', 'upcoming'],
          context.previousTasks.upcoming
        );
      }

      toast.error(error.message || 'Failed to update tags');
    }
  });
}
